/**
 * The barcode rules the app checks itself (bos-app#232): what the catalog
 * import and the repair endpoints accept as a barcode, and the EAN check
 * digit, which bos-api never checks (contract rule 7).
 *
 * Pure, so the import drawer and the Barcode issues tab share them.
 */

/** The longest barcode the import and the repair accept, in digits. */
export const BARCODE_MAX_DIGITS = 14;

const DIGITS_BARCODE = new RegExp(`^[0-9]{1,${BARCODE_MAX_DIGITS}}$`);

/**
 * Whether `code` is a barcode the import and the repair accept: 1 to 14
 * ASCII digits. A letter, a 15-digit IMEI or Excel's `3.5E+18` is not.
 */
export const isDigitsBarcode = (code: string): boolean =>
    DIGITS_BARCODE.test(code);

/**
 * What the EAN check digit says about a typed code:
 * - `valid`: an EAN-8 or EAN-13 whose last digit matches the others;
 * - `invalid`: 8 or 13 digits whose last digit doesn't match, a likely typo
 *   the owner confirms with **Use it anyway**;
 * - `notApplicable`: any other length, or not digits; nothing is checked.
 */
export type EanCheckDigitStatus = 'valid' | 'invalid' | 'notApplicable';

const EAN_LENGTHS = new Set([8, 13]);

/**
 * The GS1 check digit: from the digit next to the check digit leftwards,
 * weights 3, 1, 3, …; the check digit tops the sum up to a multiple of 10.
 */
const eanCheckDigitOf = (body: string): number => {
    let sum = 0;
    for (let i = 0; i < body.length; i += 1) {
        const fromRight = body.length - 1 - i;
        sum += Number(body[i]) * (fromRight % 2 === 0 ? 3 : 1);
    }
    return (10 - (sum % 10)) % 10;
};

/** The check-digit status of `code` (EAN-8 and EAN-13 only). */
export const eanCheckDigitStatus = (code: string): EanCheckDigitStatus => {
    if (!EAN_LENGTHS.has(code.length) || !isDigitsBarcode(code)) {
        return 'notApplicable';
    }
    const check = Number(code[code.length - 1]);
    return eanCheckDigitOf(code.slice(0, -1)) === check ? 'valid' : 'invalid';
};
